import React, { useEffect } from 'react';
import './TermsConditions.css';

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <main className="terms-page">
      <div className="container terms-container">
        <header className="terms-header">
          <h1>Terms &amp; Conditions</h1>
          <p className="terms-subtitle">
            Scan2Card &ndash; AI-Powered Lead Capture &amp; Business Card Scanner
          </p>
          <p className="terms-meta">
            Scanbiz Technologies LLP | <a href="https://scan2card.com">scan2card.com</a>
          </p>
          <p className="terms-meta">
            <strong>Last updated:</strong> 03/07/2026
          </p>
        </header>

        <section id="section-1" className="terms-section">
          <h2>1. Introduction and acceptance</h2>
          <p>
            These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your access to and
            use of the Scan2Card website (scan2card.com), mobile application and all
            related products, software, features and services (collectively, the
            &ldquo;Service&rdquo;) provided by <strong>Scanbiz Technologies LLP</strong>,
            a Limited Liability Partnership incorporated under the laws of India and
            having its registered office at Office No. 236, 2nd Floor, Adarsh Industrial
            Estate, Sahar Airport Road, Chakala, Andheri (East), Mumbai &ndash; 400099,
            Maharashtra, India (&ldquo;Scanbiz&rdquo;, &ldquo;Company&rdquo;,
            &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo;).
          </p>
          <p>
            By clicking &ldquo;Accept&rdquo;, creating an account, accessing, browsing or
            otherwise using the Service, you acknowledge that you have read, understood
            and agree to be legally bound by these Terms, our Privacy Policy, Cookie
            Policy and any other policies or guidelines that may be published by us from
            time to time, all of which are incorporated herein by reference.
          </p>
          <p>
            If you are using the Service on behalf of a company, partnership or other
            legal entity, you represent and warrant that you are duly authorized to bind
            such entity to these Terms, and references to &ldquo;you&rdquo; shall include
            such entity.
          </p>
          <p>
            If you do not agree to these Terms, you must immediately discontinue access
            to and use of the Service.
          </p>
          <p>
            These Terms constitute an electronic record under the Information Technology
            Act, 2000, the rules framed thereunder and other applicable laws governing
            electronic records and electronic contracts. These Terms do not require any
            physical, electronic or digital signature and shall have the same legal
            effect as a written agreement executed by the parties.
          </p>
        </section>

        <section id="section-2" className="terms-section">
          <h2>2. Definitions</h2>
          <ul>
            <li>
              <strong>&ldquo;Account&rdquo;</strong> means the registered account created
              by a User to access the Service.
            </li>
            <li>
              <strong>&ldquo;Applicable Laws&rdquo;</strong> means all applicable laws,
              regulations, governmental notifications, rules and industry standards
              applicable to the use of the Service.
            </li>
            <li>
              <strong>&ldquo;Lead Data&rdquo;</strong> means any personal information,
              contact details, business information, notes, photographs, files,
              recordings, scanned business cards, QR code information and other data
              uploaded, collected or processed by the User through the Service.
            </li>
            <li>
              <strong>&ldquo;Personal Data&rdquo;</strong> shall have the meaning
              assigned under applicable data protection laws, including the Digital
              Personal Data Protection Act, 2023, where applicable.
            </li>
            <li>
              <strong>&ldquo;Service&rdquo;</strong> means the Scan2Card website, mobile
              application, APIs, software, features, AI functionalities and related
              services provided by Scanbiz.
            </li>
            <li>
              <strong>&ldquo;Subscription&rdquo;</strong> means any free, trial or paid
              subscription plan offered by Scanbiz.
            </li>
            <li>
              <strong>&ldquo;User&rdquo;, &ldquo;you&rdquo;</strong> &ndash; means any
              individual or legal entity accessing or using the Service.
            </li>
          </ul>
        </section>

        <section id="section-3" className="terms-section">
          <h2>3. Eligibility</h2>
          <p>
            You represent and warrant that: (a) you are at least eighteen (18) years of
            age; (b) you possess the legal capacity to enter into binding contracts under
            applicable law; (c) all information provided during registration is true,
            accurate and complete; (d) if you access or use the Service on behalf of any
            company, partnership or other legal entity, you are duly authorised to bind
            such entity to these Terms; and (e) your use of the Service complies with all
            Applicable Laws.
          </p>
          <p>
            We reserve the right to suspend or terminate any account where we reasonably
            believe that these eligibility requirements are not satisfied.
          </p>
        </section>

        <section id="section-4" className="terms-section">
          <h2>4. Accounts and security</h2>
          <p>
            You are responsible for maintaining the confidentiality of your Account
            credentials and for all activities carried out through your Account.
          </p>
          <p>
            You agree to: (a) provide accurate, complete and up-to-date registration
            information; (b) promptly update any changes to such information; (c)
            maintain the confidentiality of your login credentials; (d) immediately
            notify Scanbiz of any suspected unauthorised access, security breach or
            misuse of your Account; and (e) take reasonable precautions to prevent
            unauthorised use of your Account.
          </p>
          <p>
            Scanbiz may require you to reset your password, suspend your Account or
            implement additional security measures where reasonably necessary to protect
            the integrity or security of the Service.
          </p>
          <p>
            Unless resulting solely from Scanbiz&rsquo;s negligence or wilful misconduct,
            you shall remain responsible for all activities undertaken through your
            Account until you notify Scanbiz of any unauthorised use.
          </p>
        </section>

        <section id="section-5" className="terms-section">
          <h2>5. Licence to use the Service</h2>
          <p>
            Subject to your continued compliance with these Terms and payment of all
            applicable fees, Scanbiz grants you a limited, non-exclusive, non-transferable,
            non-sublicensable and revocable licence during the applicable Subscription
            Term to access and use the Service solely for your internal business
            purposes.
          </p>
          <p>
            Except as expressly permitted under these Terms, you shall not: (a) copy,
            reproduce, modify or create derivative works of the Service; (b) reverse
            engineer, decompile, disassemble or otherwise attempt to discover the source
            code of the Service; (c) sublicense, lease, rent, assign, distribute,
            commercially exploit or otherwise make the Service available to any third
            party; (d) remove, obscure or alter any proprietary notices contained within
            the Service; (e) use the Service to develop, benchmark or assist in
            developing any competing product or service; or (f) access or use the Service
            in any manner that exceeds the scope of the licence granted under these
            Terms.
          </p>
          <p>
            All rights, title and interest, including all intellectual property rights in
            and to the Service, shall remain exclusively vested in Scanbiz and its
            licensors. No rights are granted to you except those expressly set out in
            these Terms.
          </p>
        </section>

        <section id="section-6" className="terms-section">
          <h2>6. Your responsibilities and acceptable use</h2>
          <p>
            You are solely responsible for the lawful use of the Service. In particular,
            you agree that:
          </p>
          <ul>
            <li>
              you will obtain any consent required from the individuals whose business
              cards you scan and whose conversations, photographs or selfies you record
              or capture, before doing so;
            </li>
            <li>
              you act as the Data Fiduciary for the Lead Data you collect and will comply
              with the DPDP Act, 2023 and all applicable laws in collecting, storing and
              using it;
            </li>
            <li>
              you will not use the Service for any unlawful, fraudulent, deceptive or
              harmful purpose, including spam or unsolicited marketing in breach of law;
            </li>
            <li>
              you will not upload malware, attempt to gain unauthorised access,
              reverse-engineer, scrape, or disrupt the Service;
            </li>
            <li>
              you will not infringe the intellectual property, privacy or other rights of
              any third party;
            </li>
            <li>
              you will not resell, sublicense or provide the Service to third parties
              except as expressly permitted.
            </li>
          </ul>
          <p>
            You acknowledge and agree that you are solely responsible for ensuring that
            all Lead Data is collected, processed, stored, shared and otherwise used in
            compliance with all Applicable Laws, including obtaining all necessary
            notices, consents and permissions where required. You further agree that you
            shall be solely responsible for any communications, marketing activities or
            follow-up actions undertaken using the Lead Data collected through the
            Service. You agree to indemnify, defend and hold harmless Scanbiz from and
            against any claims, losses, damages, liabilities, penalties, costs and
            expenses (including reasonable legal fees) arising from or relating to your
            collection, processing or use of Lead Data in violation of applicable law or
            without an appropriate lawful basis.
          </p>
        </section>

        <section id="section-7" className="terms-section">
          <h2>7. Ownership of data and content</h2>
          <p>
            As between you and us, you own your Lead Data and account content. You grant
            Scanbiz a worldwide, non-exclusive, royalty-free, revocable (subject to
            applicable law), licence to host, store, process, reproduce, transmit,
            display, back up and otherwise use such Lead Data solely to the extent
            necessary to provide, maintain, secure, support, improve and develop the
            Service, comply with applicable legal obligations, and as otherwise described
            in our Privacy Policy. Nothing in this clause transfers ownership of the Lead
            Data to Scanbiz.
          </p>
        </section>

        <section id="section-8" className="terms-section">
          <h2>8. Third-party services</h2>
          <p>
            The Service integrates with or relies on third-party services such as Amazon
            Web Services (AWS) for hosting, Google Calendar/Meet, WhatsApp, OCR/AI and
            payment providers. Your use of those services is subject to their own terms
            and privacy policies. We are not responsible for third-party services.
          </p>
        </section>

        <section id="section-9" className="terms-section">
          <h2>9. Service availability and changes</h2>
          <p>
            We aim to keep the Service available but do not guarantee uninterrupted or
            error-free operation. We may modify, suspend or discontinue any part of the
            Service, and may perform maintenance, with or without notice. We may add,
            modify, suspend or discontinue features, functionalities or integrations from
            time to time as part of the ongoing development of the Service. Unless
            expressly agreed in writing, Scanbiz does not guarantee any minimum service
            levels, uptime commitments or uninterrupted availability of the Service.
          </p>
        </section>

        <section id="section-10" className="terms-section">
          <h2>10. Suspension and termination</h2>
          <p>
            You may cease using the Service and close your Account at any time in
            accordance with the procedures made available by Scanbiz.
          </p>
          <p>
            Scanbiz may suspend, restrict or terminate your access to the Service, with
            or without prior notice, where it reasonably believes that: (a) you have
            breached these Terms; (b) your use of the Service poses a security risk to
            the Service or other users; (c) your Account has been used for fraudulent,
            unlawful or unauthorised activities; (d) suspension or termination is
            required to comply with applicable law, a court order or a request from a
            governmental authority; or (e) you have failed to pay any applicable
            subscription fees.
          </p>
          <p>
            Upon termination: (i) your licence to access and use the Service shall
            immediately cease; (ii) Scanbiz may disable your access to the Service; (iii)
            retention and deletion of Lead Data shall be governed by the Privacy Policy
            and applicable law; and (iv) any provisions of these Terms which by their
            nature are intended to survive termination, including provisions relating to
            intellectual property, confidentiality, limitation of liability, indemnity
            and dispute resolution, shall continue in full force and effect.
          </p>
        </section>

        <section id="section-11" className="terms-section">
          <h2>11. Disclaimers</h2>
          <p>
            The Service is provided on an &ldquo;as is&rdquo; and &ldquo;as
            available&rdquo; basis. To the maximum extent permitted by law, we disclaim
            all warranties, express or implied. We do not warrant that OCR/AI output is
            complete or accurate; you must verify extracted data before relying on it.
            Please see our separate Disclaimer.
          </p>
        </section>

        <section id="section-12" className="terms-section">
          <h2>12. Limitation of liability</h2>
          <p>
            To the fullest extent permitted by applicable law, Scanbiz, its partners,
            designated partners, affiliates, employees, contractors, licensors and
            service providers shall not be liable for any indirect, incidental, special,
            consequential, exemplary or punitive damages, or for any loss of profits,
            revenue, goodwill, business opportunities, anticipated savings, business
            interruption or loss or corruption of data arising out of or relating to the
            use of, or inability to use, the Service.
          </p>
          <p>
            Scanbiz&rsquo;s aggregate liability arising out of or relating to these Terms
            or the Service, whether in contract, tort (including negligence), statute or
            otherwise, shall not exceed the total subscription fees actually paid by the
            User to Scanbiz during the three (3) months immediately preceding the event
            giving rise to the claim.
          </p>
        </section>

        <section id="section-13" className="terms-section">
          <h2>13. Indemnity</h2>
          <p>
            You agree to defend, indemnify and hold harmless Scanbiz, its partners,
            designated partners, affiliates, employees, contractors and service providers
            from and against any claims, proceedings, damages, losses, liabilities,
            penalties, costs and expenses (including reasonable legal fees) arising out
            of or relating to: (a) your breach of these Terms; (b) your misuse of the
            Service; (c) your collection, processing or use of Lead Data in violation of
            applicable law; (d) any allegation that your content or Lead Data infringes
            the intellectual property, privacy or other rights of any third party; or (e)
            your negligent, fraudulent or unlawful acts or omissions.
          </p>
        </section>

        <section id="section-14" className="terms-section">
          <h2>14. Confidentiality</h2>
          <p>
            Each party agrees to keep confidential all non-public, proprietary or
            confidential information disclosed by the other party in connection with the
            Service (&ldquo;Confidential Information&rdquo;) and shall use such
            information solely for the purposes of performing or receiving the Service.
          </p>
          <p>
            Confidential Information shall not include information which: (a) is or
            becomes publicly available without breach of these Terms; (b) was lawfully
            known to the receiving party before disclosure; (c) is independently
            developed without reference to the disclosing party&rsquo;s Confidential
            Information; or (d) is lawfully received from a third party without any
            obligation of confidentiality.
          </p>
          <p>
            A party may disclose Confidential Information where required by applicable
            law or pursuant to a lawful order of a competent authority, provided that,
            where legally permissible, it gives prior notice to the other party.
          </p>
        </section>

        <section id="section-15" className="terms-section">
          <h2>15. Force majeure</h2>
          <p>
            Scanbiz shall not be liable for any failure or delay in performing its
            obligations under these Terms where such failure or delay results from events
            beyond its reasonable control, including acts of God, natural disasters,
            pandemics, epidemics, war, terrorism, civil unrest, governmental actions,
            labour disputes, cyber-attacks, internet or telecommunications failures,
            cloud infrastructure failures, failures of third-party service providers,
            power outages or other events beyond its reasonable control.
          </p>
          <p>
            The affected party shall use commercially reasonable efforts to resume
            performance as soon as reasonably practicable following the cessation of the
            relevant force majeure event.
          </p>
        </section>

        <section id="section-16" className="terms-section">
          <h2>16. Governing law and dispute resolution</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of
            India, without regard to its conflict of law principles.
          </p>
          <p>
            The parties shall endeavour to resolve any dispute, controversy or claim
            arising out of or relating to these Terms through good faith negotiations
            within thirty (30) days from the date on which either party notifies the
            other of the dispute.
          </p>
          <p>
            If the dispute is not resolved amicably within such period, it shall be
            referred to and finally resolved by arbitration in accordance with the
            Arbitration and Conciliation Act, 1996, as amended from time to time.
          </p>
          <p>
            The arbitration shall be conducted by a sole arbitrator mutually appointed by
            the parties. If the parties fail to mutually appoint the arbitrator within
            thirty (30) days, the arbitrator shall be appointed in accordance with the
            Arbitration and Conciliation Act, 1996.
          </p>
          <p>
            The seat and venue of arbitration shall be Mumbai, Maharashtra, India. The
            arbitration proceedings shall be conducted in the English language.
          </p>
          <p>
            Subject to the foregoing, the courts at Mumbai, Maharashtra shall have
            exclusive jurisdiction in relation to matters arising out of these Terms.
          </p>
        </section>

        <section id="section-17" className="terms-section">
          <h2>17. Grievance Officer</h2>
          <p>
            In accordance with applicable laws, including the Information Technology Act,
            2000 and the Digital Personal Data Protection Act, 2023, Scanbiz has
            designated a Grievance Officer to address complaints and grievances relating
            to the Service and the processing of personal data.
          </p>
          <ul>
            <li><strong>Name:</strong> Aryan Kumar</li>
            <li><strong>Designation:</strong> Grievance Officer</li>
            <li>
              <strong>Email:</strong>{' '}
              <a href="mailto:aryan@scan2card.com">aryan@scan2card.com</a>
            </li>
            <li>
              <strong>Address:</strong> Scanbiz Technologies LLP, Office No. 236, 2nd
              Floor, Adarsh Industrial Estate, Sahar Airport Road, Chakala, Andheri
              (East), Mumbai &ndash; 400099, Maharashtra, India.
            </li>
          </ul>
        </section>

        <section id="section-18" className="terms-section">
          <h2>18. Changes to these Terms</h2>
          <p>
            Scanbiz reserves the right to amend or update these Terms from time to time
            to reflect changes in applicable law, regulatory requirements, business
            practices or the functionality of the Service.
          </p>
          <p>
            Updated versions of these Terms will be published on the website and will
            become effective from the date specified therein.
          </p>
          <p>
            Where changes materially affect the rights or obligations of Users, Scanbiz
            may provide additional notice through the Service, email or other reasonable
            means.
          </p>
          <p>
            Continued access to or use of the Service after the revised Terms become
            effective shall constitute acceptance of the updated Terms.
          </p>
        </section>

        <section id="section-19" className="terms-section">
          <h2>19. Contact</h2>
          <address>
            Scanbiz Technologies LLP,<br />
            Office No. 236, 2nd Floor, Adarsh Industrial Estate,<br />
            Sahar Airport Road, Chakala, Andheri (East),<br />
            Mumbai &ndash; 400099, Maharashtra, India.
          </address>
          <p>
            <strong>Tel.:</strong> +91 72638 89831 &nbsp;|&nbsp;{' '}
            <strong>E-mail:</strong>{' '}
            <a href="mailto:aryan@scan2card.com">aryan@scan2card.com</a>
          </p>
        </section>
      </div>
    </main>
  );
};

export default TermsConditions;
